<template>
  <ReportCard :title="$t('report.members_retention_title')">
    <template #header>
      <button @click.stop="showRetentionInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.members_retention_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="relative">
      <div class="overflow-x-auto">
        <div ref="chartEl" :style="{ height: chartHeight + 'px', width: chartWidth + 'px' }"></div>
      </div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showRetentionInfo" variant="sidebar">
    <MembersRetentionInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import { DateTime } from 'luxon'
import type { RetentionResponse } from '../../responses'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import BottomSheet from '../BottomSheet.vue'
import MembersRetentionInfoSheet from '../MembersRetentionInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: RetentionResponse | null
  loading: boolean
}>()

useI18n()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const showRetentionInfo = ref(false)

const isDark = useIsDarkMode()

function formatCohortPeriod(period: string): string {
  const dt = DateTime.fromFormat(period, 'yyyy-MM')
  if (!dt.isValid) return period
  return dt.toFormat('LLL yy')
}

const chartHeight = computed(() => {
  const rows = props.response?.cohorts?.length ?? 6
  return Math.max(200, rows * 34 + 36)
})

const chartWidth = computed(() => {
  if (!props.response?.cohorts) return 300
  const maxOffset = Math.max(...props.response.cohorts.map(c => c.retention.length - 1), 0)
  const cols = Math.max(maxOffset, 6)
  return 52 + cols * 38
})

function buildChart() {
  if (!chartEl.value || !props.response?.cohorts?.length) return
  const dark = isDark.value
  const cohorts = props.response.cohorts

  const sorted = [...cohorts].sort((a, b) => a.period.localeCompare(b.period))
  const yLabels = sorted.map(c => {
    const label = formatCohortPeriod(c.period)
    return `${label}\n{size|${c.size.toLocaleString()}}`
  })

  const dataMaxOffset = Math.max(...sorted.map(c => c.retention.length - 1), 0)
  const maxOffset = Math.max(dataMaxOffset, 6)
  const xLabels = Array.from({ length: maxOffset }, (_, i) => `M${i + 1}`)

  const allRates: number[] = []
  for (const c of sorted) {
    for (let i = 1; i < c.retention.length; i++) allRates.push(c.retention[i].rate)
  }
  const rMin = allRates.length ? Math.min(...allRates) : 0
  const rMax = allRates.length ? Math.max(...allRates) : 100

  const data: [number, number, number][] = []
  const meta: Record<string, { active: number; size: number; period: string; offset: number }> = {}
  for (let yi = 0; yi < sorted.length; yi++) {
    const c = sorted[yi]
    for (let xi = 0; xi < maxOffset; xi++) {
      const ri = xi + 1
      if (ri < c.retention.length) {
        data.push([xi, yi, c.retention[ri].rate])
        meta[`${xi}-${yi}`] = { active: c.retention[ri].active, size: c.size, period: c.period, offset: xi + 1 }
      }
    }
  }

  const options: echarts.EChartsOption = {
    grid: {
      left: 52,
      right: 4,
      bottom: 24,
      top: 4,
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      position: 'bottom',
      axisLine: { show: false },
      axisTick: { show: false },
      splitArea: { show: false },
      axisLabel: {
        color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.6)',
        fontSize: 10,
        fontWeight: 'bold',
      },
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      axisLine: { show: false },
      axisTick: { show: false },
      splitArea: { show: false },
      axisLabel: {
        color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)',
        fontSize: 10,
        overflow: 'none',
        rich: {
          size: {
            fontSize: 8,
            color: dark ? 'rgba(255,255,255,0.3)' : 'rgba(107,114,128,0.5)',
            lineHeight: 12,
          },
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      show: false,
      splitNumber: 5,
      min: rMin,
      max: rMax,
      inRange: {
        color: dark
          ? ['#6B2020', '#7E4A1E', '#6B6224', '#1B5E50', '#0C5E73']
          : ['#E8564A', '#F4A261', '#E9C46A', '#2DD4A8', '#0891B2'],
      },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: dark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.96)',
      borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.6)',
      borderWidth: 1,
      borderRadius: 10,
      confine: true,
      padding: [8, 12],
      textStyle: { color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: any) => {
        const [xi, yi, rate] = params.data
        const m = meta[`${xi}-${yi}`]
        if (!m) return ''
        const cohortLabel = formatCohortPeriod(m.period)
        const targetMonth = DateTime.fromFormat(m.period, 'yyyy-MM').plus({ months: m.offset })
        const targetLabel = targetMonth.isValid ? targetMonth.toFormat('LLL yy') : ''
        return `<div style="font-weight:700;margin-bottom:4px">${cohortLabel} · M${m.offset}</div>`
          + (targetLabel ? `<div style="font-size:10px;opacity:0.5;margin-bottom:4px">${targetLabel}</div>` : '')
          + `<div style="font-size:11px;opacity:0.7">${m.active.toLocaleString()} / ${m.size.toLocaleString()}</div>`
          + `<div style="font-weight:800;font-size:14px;margin-top:2px">${rate.toFixed(1)}%</div>`
      },
    },
    series: [{
      type: 'heatmap',
      data,
      label: {
        show: true,
        fontSize: 10,
        fontWeight: 'bold',
        color: dark ? 'rgba(255,255,255,0.9)' : '#fff',
        textShadowColor: 'rgba(0,0,0,0.3)',
        textShadowBlur: 2,
        formatter: (p: any) => Math.round(p.value[2]) + '%',
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 8,
          shadowColor: 'rgba(0,0,0,0.25)',
          borderColor: dark ? '#fff' : '#1a1a1a',
          borderWidth: 2,
        },
      },
      itemStyle: {
        borderRadius: 3,
        borderColor: dark ? 'rgba(30,30,30,1)' : '#fff',
        borderWidth: 1.5,
      },
    }],
  }

  // Always re-init to pick up container size changes
  chartInstance.value?.dispose()
  chartInstance.value = echarts.init(chartEl.value)
  chartInstance.value.setOption(options)
}

function refreshChart() {
  buildChart()
}

watch(() => props.response, async () => {
  await nextTick()
  await nextTick() // DOM re-renders with new dimensions
  buildChart()
})

watch(isDark, () => buildChart())

watch(chartEl, async (el) => {
  if (el && props.response?.cohorts?.length) {
    await nextTick()
    setTimeout(buildChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
